/**
 * Customer Wallet — GDPR Art. 17 self-service erasure form.
 *
 * Surfaces the four self-service categories (marketing, profiling,
 * account anagraphics, GPS tracks) with per-category eligibility +
 * blockers loaded from /api/customers/{id}/erasure/eligibility.
 *
 * Triggers the double-confirmation flow:
 *   1. Customer ticks categories + clicks "Richiedi cancellazione"
 *   2. Frontend POSTs the request → backend emails confirmation link
 *   3. Customer clicks the email link → backend executes erasure
 *   4. UI shows the receipt summary
 *
 * Per Vercesi (28/05/2026): nessun click cancella subito i dati.
 * Sempre doppia conferma via email.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, CheckCircle2, Mail, Lock } from "lucide-react";
import { API } from "../config";

const CATEGORY_ICONS = {
  marketing: "📧",
  profiling: "📊",
  account: "👤",
  tracking: "🛰️",
};

const CATEGORY_DESCRIPTIONS_IT = {
  marketing: "Consensi commerciali, newsletter, comunicazioni promozionali.",
  profiling: "Preferenze commerciali, statistiche di utilizzo del Wallet, storico solleciti.",
  account: "Nome, cognome, indirizzo, telefono, codice fiscale. Il contratto firmato non viene cancellato.",
  tracking: "Cancellazione del collegamento tra te e i tracciati GPS del veicolo. I tracciati restano in forma anonima per finalità statistiche.",
};


const ErasureRequestPage = () => {
  // The customer's UUID is passed via URL: /erasure?cid=...
  const params = new URLSearchParams(window.location.search);
  const customerId = params.get("cid") || params.get("customer_id");

  const [eligibility, setEligibility] = useState(null);
  const [selected, setSelected] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmStep, setConfirmStep] = useState(0); // 0 = idle, 1 = confirming
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) {
      toast.error("ID cliente mancante nell'URL");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/customers/${customerId}/erasure/eligibility`);
        setEligibility(r.data);
      } catch (e) {
        toast.error(`Impossibile caricare: ${e.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [customerId]);

  const toggleCategory = (cat) => {
    setSelected(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const submit = async () => {
    if (selectedCount === 0) {
      toast.error("Seleziona almeno una categoria");
      return;
    }
    if (confirmStep === 0) {
      setConfirmStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const categories = Object.keys(selected).filter(k => selected[k]);
      await axios.post(`${API}/customers/${customerId}/erasure/request`, { categories });
      setSubmitted(true);
      toast.success("Richiesta inviata — controlla la tua email");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
      setConfirmStep(0);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!eligibility) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-6 max-w-md">
          <p className="text-red-300 text-sm">Impossibile recuperare lo stato di cancellazione. Verifica l'URL e riprova.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4 py-10">
        <div className="bg-[#111] border border-lojack-blue-500/40 rounded-2xl p-8 max-w-md text-center" data-testid="erasure-submitted">
          <div className="inline-flex w-14 h-14 rounded-full bg-lojack-blue-500/15 items-center justify-center mb-4">
            <Mail size={26} className="text-lojack-blue-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Controlla la tua email</h2>
          <p className="text-gray-400 text-sm leading-relaxed">
            Ti abbiamo inviato un link di conferma. Cliccaci sopra entro <strong className="text-white">24 ore</strong> per completare la cancellazione.<br />
            La procedura è irreversibile e può essere usata <strong className="text-white">una sola volta</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="erasure-request-page">
      <header className="border-b border-gray-900 py-6">
        <div className="max-w-2xl mx-auto px-6 flex items-center gap-3">
          <ShieldCheck size={20} className="text-lojack-blue-400" />
          <div>
            <h1 className="text-lg font-semibold text-white">Cancellazione dei tuoi dati</h1>
            <p className="text-gray-500 text-xs">GDPR Art. 17 — Diritto all'oblio</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        <div className="bg-lojack-blue-500/5 border border-lojack-blue-500/30 rounded-xl p-4 text-sm text-lojack-blue-300 leading-relaxed">
          Seleziona qui sotto le categorie di dati che desideri cancellare. La cancellazione del <strong>contratto firmato</strong> e dei <strong>dati conservati per obblighi di legge</strong> richiede una richiesta separata al titolare del trattamento e non è disponibile da questa pagina.
        </div>

        <div className="space-y-3">
          {eligibility.categories.map(c => {
            const isSelected = !!selected[c.category];
            const eligible = c.eligible;
            return (
              <label
                key={c.category}
                className={`block rounded-xl border p-4 transition cursor-pointer ${
                  !eligible
                    ? "bg-gray-700/20 border-gray-700 cursor-not-allowed opacity-60"
                    : isSelected
                      ? "bg-lojack-blue-500/10 border-lojack-blue-500/50"
                      : "bg-[#111] border-gray-800 hover:border-lojack-blue-500/40"
                }`}
                data-testid={`erasure-category-${c.category}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => eligible && toggleCategory(c.category)}
                    disabled={!eligible}
                    className="mt-1 w-4 h-4 accent-lojack-blue-500"
                    data-testid={`erasure-checkbox-${c.category}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xl">{CATEGORY_ICONS[c.category]}</span>
                      <p className="text-white font-semibold text-sm">{c.label}</p>
                      {!eligible && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-lojack-clause-500/15 text-lojack-clause-300 border border-lojack-clause-500/40 flex items-center gap-1">
                          <Lock size={9} /> Non disponibile
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-xs mt-1.5 leading-relaxed">
                      {CATEGORY_DESCRIPTIONS_IT[c.category]}
                    </p>
                    {!eligible && c.blockers.length > 0 && (
                      <p className="text-lojack-clause-400 text-xs mt-2 flex items-start gap-1">
                        <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                        {c.blockers[0]}
                      </p>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {confirmStep === 1 && (
          <div className="bg-lojack-clause-500/10 border border-lojack-clause-500/40 rounded-xl p-4 text-sm" data-testid="erasure-confirm-step">
            <p className="text-lojack-clause-300 font-semibold mb-1">Sei sicuro?</p>
            <p className="text-lojack-clause-200/80 text-xs leading-relaxed">
              Ti invieremo subito una email con un link di conferma. Cliccando il link la cancellazione delle categorie selezionate sarà <strong>irreversibile</strong>.
            </p>
          </div>
        )}

        <div className="flex items-center gap-3 justify-end">
          {confirmStep === 1 && (
            <button
              onClick={() => setConfirmStep(0)}
              className="px-4 py-2 bg-[#1a1a1a] border border-gray-700 rounded-lg text-gray-300 text-sm"
              data-testid="erasure-cancel-btn"
            >
              Annulla
            </button>
          )}
          <button
            onClick={submit}
            disabled={submitting || selectedCount === 0}
            className="px-5 py-2.5 bg-lojack-blue-500 hover:bg-lojack-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg flex items-center gap-2"
            data-testid="erasure-submit-btn"
          >
            {confirmStep === 0
              ? `Richiedi cancellazione${selectedCount > 0 ? ` (${selectedCount})` : ""}`
              : submitting ? "Invio in corso…" : "Confermo, invia email"}
          </button>
        </div>

        <p className="text-gray-600 text-[11px] text-center leading-relaxed">
          Procedura conforme al GDPR Art. 17. Codice fiscale, dati identificativi e contratto firmato verranno conservati per gli obblighi di legge (art. 2220 c.c.). Per richiederne la cancellazione contatta il titolare del trattamento.
        </p>
      </main>
    </div>
  );
};

export default ErasureRequestPage;
